import React, { useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp as amplifyConfirmSignUp, AuthError } from 'aws-amplify/auth';
import PianoBackground from '../../assets/images/pianoBackground.png';
import Logo from 'Components/Logo';
import { CustomCircularProgress } from 'Components/StyledComponents';
import CheckIcon from '@mui/icons-material/Check';
import { handleAuthErrors } from 'Utils/Forms';
import { detectDevice } from 'Utils';
import { isCapacitorNative } from 'Utils/Constants';
import * as authActions from 'Actions/auth';
import * as eventActions from 'Actions/events';
import { AppDispatch } from 'Reducers';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';

const Verification: React.FC = () => {
  const [verified, setVerified] = React.useState(false);
  const [error, setError] = React.useState('');
  const [isConfirming, setIsConfirming] = React.useState(true);
  const [verifiedEmail, setVerifiedEmail] = React.useState<string | undefined>(undefined);

  const confirmStarted = React.useRef(false);
  const { user, tempSignUpPass, logInUsername, processPending, setPendingEmailOrPhone } =
    useAuthWrapperContext();

  const navigate = useNavigate();
  const search = useLocation().search;
  const dispatch = useDispatch<AppDispatch>();

  // The confirmation email opens this page in mobile Safari, not the native app.
  // On an iPhone/iPad we offer a deep link back into the installed app instead of
  // continuing the session in the browser. (detectDevice handles iPadOS-as-Mac.)
  const device = detectDevice();
  const showOpenInApp = !isCapacitorNative() && (device === 'iphone' || device === 'ipad');

  useEffect(() => {
    if (user) {
      navigate('/roadmap');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (confirmStarted.current) return;
    confirmStarted.current = true;

    const query = new URLSearchParams(search);
    const username = query.get('user_name');
    const confirmationCode = query.get('confirmation_code');
    const emailParam = query.get('email');

    if (username && confirmationCode && emailParam) {
      const normalizedEmail = emailParam.replace(/ /g, '+');
      setVerifiedEmail(normalizedEmail);

      amplifyConfirmSignUp({
        username,
        confirmationCode,
      })
        .then(() => {
          setVerified(true);
          dispatch(eventActions.accountVerifiedAction());
        })
        .catch((rawError: Error) => {
          const authError = rawError as unknown as AuthError;
          const alreadyConfirmed =
            authError.name === 'NotAuthorizedException' &&
            String(authError.message || '').includes('Current status is CONFIRMED');
          if (alreadyConfirmed) {
            setVerified(true);
            dispatch(eventActions.accountVerifiedAction());
            return;
          }

          const err = handleAuthErrors(authError);
          dispatch(authActions.setTempSignUpPass(undefined));
          if (err?.name) setError(err.name);
          if (err?.unknown) setError(err.unknown);
        })
        .finally(() => {
          setIsConfirming(false);
        });
    } else {
      setIsConfirming(false);
      navigate('/signin');
    }
  }, [dispatch, navigate, search]);

  const handleContinue = React.useCallback(() => {
    const storedPass = tempSignUpPass || localStorage.getItem('tempSignUpPass') || undefined;

    if (verifiedEmail && storedPass) {
      logInUsername(verifiedEmail, storedPass, '');
      navigate('/roadmap');
      return;
    }

    if (verifiedEmail) {
      setPendingEmailOrPhone?.(verifiedEmail);
    }
    navigate('/signin');
  }, [tempSignUpPass, verifiedEmail, logInUsername, navigate, setPendingEmailOrPhone]);

  // On web/native (everywhere except a mobile browser, where we hand off to the app
  // via the "Continue in App" button), auto-authenticate once verification succeeds
  // and go straight to the roadmap — no button to tap.
  const autoContinued = React.useRef(false);
  useEffect(() => {
    if (showOpenInApp || autoContinued.current) return;
    if (!verified || error || isConfirming || processPending) return;
    autoContinued.current = true;
    handleContinue();
  }, [showOpenInApp, verified, error, isConfirming, processPending, handleContinue]);

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        style={{
          height: '100vh',
          opacity: 0.3,
          position: 'absolute',
        }}
        src={PianoBackground}
        alt="piano-background"
      ></img>
      <Box className="page-div-styles">
        <Box className="filter-div-styles">
          <Box className="sign-in-div-styles">
            <Logo />
            <Box className="desc-container welcome-container">
              <Typography variant="h3" className="title-welcome" style={{ marginTop: '50px' }}>
                {!verified && !error && 'Verification Pending'}
                {verified && 'Email Verified'}
                {error && 'Verification Error'}
              </Typography>
              {((!verified && !error && isConfirming) || processPending) && (
                <CustomCircularProgress />
              )}
              {verified && !error && !processPending && !isConfirming && (
                <CheckIcon color="success" sx={{ height: '80px', width: '80px' }} />
              )}
              {error && <Typography color="red">{error}</Typography>}
              {verified && !error && !processPending && !isConfirming && showOpenInApp && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography sx={{ marginTop: '20px', marginBottom: '14px' }} color="#00C2FF">
                    Your email is verified. Open the MuseFlow app to sign in.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ minWidth: '220px' }}
                    component="a"
                    href={`museflow://signin${
                      verifiedEmail ? `?email=${encodeURIComponent(verifiedEmail)}` : ''
                    }`}
                  >
                    Continue in App
                  </Button>
                  <Button
                    variant="text"
                    sx={{ minWidth: '220px', marginTop: '8px' }}
                    onClick={handleContinue}
                  >
                    Continue in browser
                  </Button>
                </Box>
              )}
              {verified && !error && !processPending && !isConfirming && !showOpenInApp && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography sx={{ marginTop: '20px', marginBottom: '14px' }} color="#00C2FF">
                    Your email is verified. Signing you in…
                  </Typography>
                  <CustomCircularProgress />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Verification;
